import { API_URL } from '@/lib/api';

// ─── Dogs API Functions ─────────────────────────────────────────────
// Standalone fetch functions extracted from DogsContext.

import type { DogProfile } from '@/types/dog-profile';

export async function fetchDogProfiles(token: string): Promise<DogProfile[]> {
  const response = await fetch(`${API_URL}/dog`, {
    headers: { Authorization: `Bearer ${token}` },
  });

  if (!response.ok) {
    throw new Error(`Failed to fetch dog profiles (${response.status})`);
  }

  const data = await response.json();
  if (Array.isArray(data)) return data;
  if (data && typeof data === 'object') return [data];
  return [];
}

export async function createDogProfileAPI(
  token: string,
  profile: Omit<DogProfile, 'id' | 'created_at' | 'updated_at'>,
): Promise<DogProfile> {
  let createPayload: any = { ...profile };
  // remove any legacy `photo` key if present
  if (createPayload.photo) delete createPayload.photo;

  const isLocalImage = typeof profile.image === 'string' && profile.image && !profile.image.startsWith('http');
  if (isLocalImage) {
    createPayload.image = undefined;
  }

  const response = await fetch(`${API_URL}/dog`, {
    method: 'POST',
    headers: {
      'Content-Type': 'application/json',
      Authorization: `Bearer ${token}`,
    },
    body: JSON.stringify(createPayload),
  });

  if (!response.ok) {
    const errorData = await response.json().catch(() => ({}));
    throw new Error(errorData.detail?.[0]?.msg || 'Failed to create dog profile');
  }

  const data = await response.json();

  // If we had a local photo, upload it now and patch the dog with the returned URL
  if (isLocalImage && typeof profile.image === 'string') {
    try {
      const uri = profile.image as string;
      const uriParts = uri.split('/');
      const fileName = uriParts[uriParts.length - 1] || `dog_${Date.now()}.jpg`;
      const extMatch = fileName.match(/\.([a-zA-Z0-9]+)(?:\?.*)?$/);
      const ext = extMatch ? extMatch[1].toLowerCase() : 'jpg';
      const mimeType = ext === 'png' ? 'image/png' : 'image/jpeg';

      const form = new FormData();
      // @ts-ignore
      form.append('file', { uri, name: fileName, type: mimeType });
      form.append('filename', fileName);
      form.append('dog_id', data.id);
      form.append('trail_id', '');
      form.append('user_id', '');

      const uploadResp = await fetch(`${API_URL}/uploads`, {
        method: 'POST',
        headers: {
          Authorization: `Bearer ${token}`,
          accept: 'application/json',
        },
        body: form as any,
      });

      if (uploadResp.ok) {
        const uploadData = await uploadResp.json();
        const publicUrl = uploadData.public_url || uploadData.publicUrl || uploadData.url;
        if (publicUrl) {
          const patchResp = await fetch(`${API_URL}/dog/${data.id}`, {
            method: 'PUT',
            headers: {
              'Content-Type': 'application/json',
              Authorization: `Bearer ${token}`,
            },
            body: JSON.stringify({ ...data, image: publicUrl }),
          });
          if (patchResp.ok) {
            return patchResp.json();
          }
        }
      }
    } catch (err) {
      console.error('Error uploading dog photo after create:', err);
    }
  }

  return data;
}

export async function updateDogProfileAPI(
  token: string,
  profile: DogProfile,
): Promise<DogProfile> {
  let profileToSend: any = { ...profile };
  if (profileToSend.photo) delete profileToSend.photo;

  // If photo is a local URI, upload it first
  if (typeof profile.image === 'string' && profile.image && !profile.image.startsWith('http')) {
    try {
      const uri = profile.image as string;
      const uriParts = uri.split('/');
      const fileName = uriParts[uriParts.length - 1] || `dog_${Date.now()}.jpg`;
      const extMatch = fileName.match(/\.([a-zA-Z0-9]+)(?:\?.*)?$/);
      const ext = extMatch ? extMatch[1].toLowerCase() : 'jpg';
      const mimeType = ext === 'png' ? 'image/png' : 'image/jpeg';

      const form = new FormData();
      // @ts-ignore
      form.append('file', { uri, name: fileName, type: mimeType });
      form.append('filename', fileName);
      form.append('dog_id', profile.id ?? '');
      form.append('trail_id', '');
      form.append('user_id', '');

      const uploadResp = await fetch(`${API_URL}/uploads`, {
        method: 'POST',
        headers: {
          Authorization: `Bearer ${token}`,
          accept: 'application/json',
        },
        body: form as any,
      });

      if (!uploadResp.ok) {
        const errText = await uploadResp.text();
        throw new Error(`Upload failed: ${uploadResp.status} ${errText}`);
      }

      const uploadData = await uploadResp.json();
      const publicUrl = uploadData.public_url || uploadData.publicUrl || uploadData.url;
      if (!publicUrl) throw new Error('Upload did not return a public URL');
      profileToSend.image = publicUrl;
    } catch (err) {
      console.error('Error uploading dog photo during update:', err);
      throw err;
    }
  }

  const response = await fetch(`${API_URL}/dog/${profile.id}`, {
    method: 'PUT',
    headers: {
      'Content-Type': 'application/json',
      Authorization: `Bearer ${token}`,
    },
    body: JSON.stringify(profileToSend),
  });

  if (!response.ok) {
    const errorData = await response.json().catch(() => ({}));
    throw new Error(errorData.detail?.[0]?.msg || 'Failed to update dog profile');
  }

  return response.json();
}